import {
  AnomalyCameraType,
  AnomalyFileType,
  AnomalyGuids,
  AnomalyPerspectiveCamera,
  AnomalyScene,
  ObjectAnomalyInformation,
} from '@api/twin/AnomalyApi';
import { Vector3 } from 'three';

/*
  Default Camera when the camera is not a perspective camera
  Match the camera values when first loading
*/
export const DEFAULT_ANOMALY_PERSPECTIVE_CAMERA: AnomalyPerspectiveCamera = {
  cameraViewPoint: new Vector3(29.945303557184424, -34.44530296113798, 29.27030332174607),
  cameraDirection: new Vector3(-0.5773502691896257, 0.5773502691896261, -0.5773502691896257),
  type: AnomalyCameraType.PERSPECTIVE,
  cameraUpVector: new Vector3(0, 0, 1),
  aspectRatio: 3.2764976958525347,
  fieldOfView: 50,
};

const getNotSelectedVisibleObjects = (
  visibleGuids: Array<string>,
  selectionGuids: Array<string>,
) => {
  return visibleGuids.filter((guid) => !selectionGuids.includes(guid));
};

const getShorterGuidList = (
  hasMoreHiddenGuids: boolean,
  getVisibleNotSelectedGuids: () => Array<string>,
  getHiddenGuids: () => Array<string>,
) => {
  return {
    guidList: hasMoreHiddenGuids ? getVisibleNotSelectedGuids() : getHiddenGuids(),
    visibility: hasMoreHiddenGuids
      ? ObjectAnomalyInformation.isVisible
      : ObjectAnomalyInformation.isHidden,
  };
};

export const buildAnomalyGuidsDictionnary = (
  selectionGuids: Array<string>,
  visibleGuids: Array<string>,
  hiddenGuids: Array<string>,
) => {
  const anomalyGuids: AnomalyGuids = {};

  const hasMoreGuidsHidden = hiddenGuids.length > visibleGuids.length - selectionGuids.length;

  anomalyGuids['DefaultVisibility'] = ObjectAnomalyInformation.isVisible;

  if (hasMoreGuidsHidden) {
    anomalyGuids['DefaultVisibility'] = ObjectAnomalyInformation.isHidden;
  }

  selectionGuids.forEach((guid) => {
    anomalyGuids[guid] = ObjectAnomalyInformation.isSelected;
  });

  const { guidList, visibility } = getShorterGuidList(
    hasMoreGuidsHidden,
    () => getNotSelectedVisibleObjects(visibleGuids, selectionGuids),
    () => hiddenGuids,
  );

  guidList.forEach((guid) => {
    anomalyGuids[guid] = visibility;
  });

  return anomalyGuids;
};

export const createAnomalyScene = (anomalyGuids: AnomalyGuids) => {
  const anomalyScene: AnomalyScene = Object.entries(anomalyGuids).reduce<{
    visible: Array<string>;
    hidden: Array<string>;
    selected: Array<string>;
    defaultVisibility: ObjectAnomalyInformation;
  }>(
    (acc, [key, value]) => {
      if (key === 'DefaultVisibility') {
        acc.defaultVisibility = value;
      } else if (value === 'isVisible') {
        acc.visible.push(key);
      } else if (value === 'isHidden') {
        acc.hidden.push(key);
      } else if (value === 'isSelected') {
        acc.selected.push(key);
      }

      return acc;
    },
    {
      visible: [],
      hidden: [],
      selected: [],
      defaultVisibility: ObjectAnomalyInformation.isVisible,
    },
  );

  return anomalyScene;
};

export const defineFileImportUrl = (fileExtension: string) => {
  switch (fileExtension) {
    case 'bcf':
    case 'bcfzip':
      return AnomalyFileType.BCF;
    case 'xls':
    case 'xlsx':
      return AnomalyFileType.Excel;
    default:
      throw new Error('Extension non supportée');
  }
};
